import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Loader2,
  Save,
  AlertCircle,
  Package,
  IndianRupee,
  Users,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  getSubscriptionPlanById,
  updateSubscriptionPlan,
} from "@/services/subscriptionService";

/**
 * 💡 Helper component for features
 */
const FeatureToggle = ({ featureKey, label, checked, onChange, disabled = false }) => (
  <div className="flex items-center justify-between rounded-md border p-4 hover:shadow-sm transition-shadow">
    <label
      htmlFor={featureKey}
      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
    >
      {label}
    </label>
    <Switch
      id={featureKey}
      checked={checked}
      disabled={disabled}
      onCheckedChange={(value) => onChange(featureKey, value)}
      className="data-[state=checked]:bg-primary" // ← Changed from green-500
    />
  </div>
);

/**
 * 📝 Main Edit Plan component
 */
const EditPlan = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    plan_name: "",
    description: "",
    price_per_user_monthly: 0,
    price_per_user_annual: 0,
    is_active: true,
    is_recurring: true,
    features: {
      employee_limit: 10,
      admin_users_limit: 1,
      trial_days: 0,

      //Platform & Security
      custom_branding: false,
      multiple_branches: false,
      role_based_access: false,
      activity_logs: false,
      ip_restrictions: false,
      roles_and_access: false,

      // Employee & Core Management
      document_upload: false,
      department_mapping: false,
      bulk_import: false,
      excel_pdf_export: false,
      employee_portal: false,
      event_management: false,

      // Attendance
      web_punch: false,
      geo_fencing: false,
      biometric_integration: false,
      multi_shift_support: false,
      regularization_requests: false,
      late_early_punch_rules: false,
      performance_management: false,
      performance_ratings: false,
      overtime_management: false,

      //Payroll & Leave Management
      basic_leave_management: true,
      custom_leave_management: false,
      leave_encashment: false,
      payroll_analytics: false,
      basic_payroll: false,
      advanced_payroll: false,
      bank_advice: false,
      multi_bank_accounts: false,
      loan_management: false,
      form_16_generation: false,
    },
  });

  useEffect(() => {
    fetchPlanDetails();
  }, [planId]);

  const fetchPlanDetails = async () => {
    try {
      setLoading(true);
      const plan = await getSubscriptionPlanById(planId);

      const mergedFeatures = {
        ...formData.features,
        ...plan.features,
      };

      setFormData({
        plan_name: plan.plan_name || "",
        description: plan.description || "",
        price_per_user_monthly: plan.price_per_user_monthly || 0,
        price_per_user_annual: plan.price_per_user_annual || 0,
        is_active: plan.is_active !== undefined ? plan.is_active : true,
        is_recurring: plan.is_recurring !== undefined ? plan.is_recurring : true,
        features: mergedFeatures,
      });
    } catch (error) {
      console.error("Error fetching plan:", error);
      setError(error.response?.data?.detail || "Failed to load plan details");
      toast.error("Failed to load plan details");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFeatureChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [field]: value,
      },
    }));
  };

  const validateForm = () => {
    if (!formData.plan_name.trim()) {
      toast.error("Plan name is required");
      return false;
    }

    const monthlyPrice = parseFloat(formData.price_per_user_monthly);
    const annualPrice = parseFloat(formData.price_per_user_annual);

    if (isNaN(monthlyPrice) || monthlyPrice < 0) {
      toast.error("Monthly price must be a non-negative number");
      return false;
    }
    if (isNaN(annualPrice) || annualPrice < 0) {
      toast.error("Annual price must be a non-negative number");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);

      const payload = {
        plan_name: formData.plan_name,
        description: formData.description,
        price_per_user_monthly: parseFloat(formData.price_per_user_monthly),
        price_per_user_annual: parseFloat(formData.price_per_user_annual),
        is_active: formData.is_active,
        is_recurring: formData.is_recurring,
        features: formData.features,
      };

      await updateSubscriptionPlan(planId, payload);
      toast.success("Plan updated successfully!");
      navigate("/super-admin/plans");
    } catch (error) {
      console.error("Error updating plan:", error);
      toast.error(error.response?.data?.detail || "Failed to update plan");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen-minus-header">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="ml-3 text-lg text-muted-foreground">Loading plan details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto py-12">
        <Card className="shadow-lg border-red-200">
          <CardContent className="p-12 text-center">
            <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h3 className="text-2xl font-bold text-red-700 mb-2">
              Error Loading Plan
            </h3>
            <p className="text-muted-foreground mb-8">{error}</p>
            <Button
              onClick={() => navigate("/super-admin/plans")}
              variant="outline"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Plans List
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/super-admin/plans")}
            className="text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
              Edit Subscription Plan
            </h1>
            <p className="mt-1 text-lg text-primary font-semibold">
              {formData.plan_name}
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/super-admin/plans")}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="submit" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      <Separator />

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Column 1 */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="shadow-lg">
              <CardHeader className="flex flex-row items-center space-x-3 pb-2">
                <Package className="h-6 w-6 text-primary" />
                <CardTitle>Plan Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="plan_name">Plan Name *</Label>
                  <Input
                    id="plan_name"
                    value={formData.plan_name}
                    onChange={(e) =>
                      handleInputChange("plan_name", e.target.value)
                    }
                    placeholder="e.g., Professional"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Brief description of the plan"
                    rows={3}
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-md border bg-muted">
                  <Label htmlFor="is_active">
                    Plan Status (Active/Inactive)
                  </Label>
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(value) =>
                      handleInputChange("is_active", value)
                    }
                    className="data-[state=checked]:bg-primary"
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-md border bg-muted">
                  <div className="flex flex-col">
                    <Label htmlFor="is_recurring">Recurring Subscription</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.is_recurring ? "Auto-renews each cycle" : "One-time purchase only"}
                    </p>
                  </div>
                  <Switch
                    id="is_recurring"
                    checked={formData.is_recurring}
                    onCheckedChange={(value) =>
                      handleInputChange("is_recurring", value)
                    }
                    className="data-[state=checked]:bg-primary"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardHeader className="flex flex-row items-center space-x-3 pb-2">
                <IndianRupee className="h-6 w-6 text-primary" />
                <CardTitle>Pricing & Limits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="monthly_price">Monthly Price (₹) *</Label>
                    <Input
                      id="monthly_price"
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      value={formData.price_per_user_monthly}
                      onChange={(e) =>
                        handleInputChange(
                          "price_per_user_monthly",
                          e.target.value,
                        )
                      }
                      placeholder="999"
                      min="0"
                      step="1"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="annual_price">Annual Price (₹) *</Label>
                    <Input
                      id="annual_price"
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      value={formData.price_per_user_annual}
                      onChange={(e) =>
                        handleInputChange(
                          "price_per_user_annual",
                          e.target.value,
                        )
                      }
                      placeholder="9999"
                      min="0"
                      step="1"
                      required
                    />
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="employee_limit">Employee Limit</Label>
                    <Input
                      id="employee_limit"
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      value={formData.features.employee_limit}
                      onChange={(e) =>
                        handleFeatureChange(
                          "employee_limit",
                          parseInt(e.target.value),
                        )
                      }
                      placeholder="10"
                      min="-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Use -1 for unlimited
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="admin_limit">Admin Users Limit</Label>
                    <Input
                      id="admin_limit"
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      value={formData.features.admin_users_limit}
                      onChange={(e) =>
                        handleFeatureChange(
                          "admin_users_limit",
                          parseInt(e.target.value),
                        )
                      }
                      placeholder="1"
                      min="-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Use -1 for unlimited
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="trial_days">Trial Days</Label>
                  <Input
                    id="trial_days"
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    value={formData.features.trial_days || 0}
                    onChange={(e) =>
                      handleFeatureChange(
                        "trial_days",
                        parseInt(e.target.value),
                      )
                    }
                    placeholder="0"
                    min="0"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    0 for no trial. Use value like 30 for free plan.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Column 2/3: Features */}
          <div className="lg:col-span-2 space-y-8">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CheckCircle className="h-6 w-6 text-primary" />{" "}
                  {/* ← Changed from green-500 */}
                  <span>Feature Configuration</span>
                </CardTitle>
                <CardDescription>
                  Enable or disable specific modules and advanced
                  functionalities for this plan.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Platform & Security */}
                <div>
                  <h4 className="text-lg font-bold mb-3 border-b pb-1">
                    Platform & Security
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FeatureToggle
                      featureKey="custom_branding"
                      label="Custom Company Branding"
                      checked={formData.features.custom_branding}
                      onChange={handleFeatureChange}
                    />
                    {/* company logo */}
                    <FeatureToggle
                      featureKey="multiple_branches"
                      label="Multiple Branches/Locations"
                      checked={formData.features.multiple_branches}
                      onChange={handleFeatureChange}
                    />
                    {/* multiple work location */}
                    <FeatureToggle
                      featureKey="role_based_access"
                      label="Role-based Access Control (RBAC)"
                      checked={formData.features.role_based_access}
                      onChange={handleFeatureChange}
                    />
                    {/* role & permissions */}
                    <FeatureToggle
                      featureKey="roles_and_access"
                      label="Access Control Management"
                      checked={formData.features.roles_and_access}
                      onChange={handleFeatureChange}
                    />

                    <FeatureToggle
                      featureKey="activity_logs"
                      label="Comprehensive Activity Logs"
                      checked={formData.features.activity_logs}
                      onChange={handleFeatureChange}
                    />
                    {/* Activity logs page */}
                    <FeatureToggle
                      featureKey="ip_restrictions"
                      label="IP/Device Restrictions"
                      checked={formData.features.ip_restrictions}
                      onChange={handleFeatureChange}
                    />
                    {/* IP restrictions */}
                  </div>
                </div>
                <Separator />

                {/* Employee & Core Management */}
                <div>
                  <h4 className="text-lg font-bold mb-3 border-b pb-1">
                    Employee & Core Management
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FeatureToggle
                      featureKey="document_upload"
                      label="Document/KYC Management"
                      checked={formData.features.document_upload}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="department_mapping"
                      label="Job & Department Mapping"
                      checked={formData.features.department_mapping}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="bulk_import"
                      label="Bulk Employee Import/Export"
                      checked={formData.features.bulk_import}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="excel_pdf_export"
                      label="Report Export (Excel/PDF)"
                      checked={formData.features.excel_pdf_export}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="employee_portal"
                      label="Employee Portal Access"
                      checked={formData.features.employee_portal}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="event_management"
                      label="Event Management"
                      checked={formData.features.event_management}
                      onChange={handleFeatureChange}
                    />
                  </div>
                </div>
                <Separator />

                {/* Time & Attendance */}
                <div>
                  <h4 className="text-lg font-bold mb-3 border-b pb-1">
                    Time & Attendance
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <FeatureToggle
                      featureKey="web_punch"
                      label="Web Punch (IN/OUT)"
                      checked={formData.features.web_punch}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="geo_fencing"
                      label="Geo-fenced Mobile Punch"
                      checked={formData.features.geo_fencing}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="biometric_integration"
                      label="Biometric Integration"
                      checked={formData.features.biometric_integration}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="multi_shift_support"
                      label="Multi-Shift Support"
                      checked={formData.features.multi_shift_support}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="regularization_requests"
                      label="Attendance Regularization"
                      checked={formData.features.regularization_requests}
                      onChange={handleFeatureChange}
                    />
                    {/* Grace period,  */}
                    <FeatureToggle
                      featureKey="late_early_punch_rules"
                      label="Late/Early/Half-Day Rules"
                      checked={formData.features.late_early_punch_rules}
                      onChange={handleFeatureChange}
                    />
                    {/* Grace period,  */}
                    <FeatureToggle
                      featureKey="performance_ratings"
                      label="Performance Ratings"
                      checked={formData.features.performance_ratings}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="performance_management"
                      label="Performance Management Module"
                      checked={formData.features.performance_management}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="overtime_management"
                      label="Overtime Management"
                      checked={formData.features.overtime_management}
                      onChange={handleFeatureChange}
                    />
                  </div>
                </div>
                <Separator />

                {/* Leave & Payroll */}
                <div>
                  <h4 className="text-lg font-bold mb-3 border-b pb-1">
                    Leave & Payroll
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {/* Default casual leave only, not configurable */}
                    <FeatureToggle
                      featureKey="basic_leave_management"
                      label="Basic Leave Management"
                      checked={formData.features.basic_leave_management}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="custom_leave_management"
                      label="Advanced Leave Management"
                      checked={formData.features.custom_leave_management}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="leave_encashment"
                      label="Leave Encashment Processing"
                      checked={formData.features.leave_encashment}
                      onChange={handleFeatureChange}
                    />
                    {/* Payroll analytics */}
                    <FeatureToggle
                      featureKey="payroll_analytics"
                      label="Payroll analytics"
                      checked={formData.features.payroll_analytics}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="basic_payroll"
                      label="Basic Payroll Processing"
                      checked={formData.features.basic_payroll}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="advanced_payroll"
                      label="Advanced Payroll Processing"
                      checked={formData.features.advanced_payroll}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="multi_bank_accounts"
                      label="Multiple Bank Accounts Support"
                      checked={formData.features.multi_bank_accounts}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="loan_management"
                      label="Loan & Advance Management"
                      checked={formData.features.loan_management}
                      onChange={handleFeatureChange}
                    />
                    <FeatureToggle
                      featureKey="form_16_generation"
                      label="Form 16 Generation"
                      checked={formData.features.form_16_generation}
                      onChange={handleFeatureChange}
                    />
                  </div>
                </div>
                <Separator />
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditPlan;
